export type GuardOutcome = 'granted' | 'denied' | 'unknown' | 'no-result';

export interface OutcomeInputs {
    /** Person lookup returned a personId. */
    matched: boolean;
    /** A classification/theme arrived (i.e. did not time out). */
    hasTheme: boolean;
    /** The matched theme's deniesAccess flag. */
    deniesAccess: boolean;
    /** The person lookup errored (network/server). */
    lookupFailed: boolean;
}

export interface OutcomeResult {
    outcome: GuardOutcome;
    showPersonInfo: boolean;
    recordInOut: boolean;
}

/**
 * Resolves the guard scan outcome. Fallbacks deliberately err toward NOT
 * recording an IN/OUT scan when access or identity cannot be confirmed.
 */
export function resolveOutcome(i: OutcomeInputs): OutcomeResult {
    if (i.matched) {
        if (!i.hasTheme) return { outcome: 'no-result', showPersonInfo: true, recordInOut: false };
        if (i.deniesAccess) return { outcome: 'denied', showPersonInfo: true, recordInOut: false };
        return { outcome: 'granted', showPersonInfo: true, recordInOut: true };
    }
    return { outcome: i.lookupFailed ? 'no-result' : 'unknown', showPersonInfo: false, recordInOut: false };
}

export interface ResultActionInputs {
    outcome: GuardOutcome;
    /** An IN detector is configured. */
    canIn: boolean;
    /** An OUT detector is configured. */
    canOut: boolean;
    /** Setting: offer Ignore alongside In/Out. */
    showIgnoreSetting: boolean;
}

export interface ResultActions {
    showIn: boolean;
    showOut: boolean;
    showIgnore: boolean;
    ignoreLabel: 'Ignore' | 'Dismiss';
}

/**
 * Resolves which action buttons a guard result offers. In/Out only appear for
 * a granted outcome and only when their detector is configured. The Ignore
 * button can be hidden by configuration, but when no In/Out button is shown it
 * is always kept (as "Dismiss") so the overlay can never dead-end.
 */
export function resolveResultActions(i: ResultActionInputs): ResultActions {
    const showIn = i.outcome === 'granted' && i.canIn;
    const showOut = i.outcome === 'granted' && i.canOut;
    const onlyDismiss = !showIn && !showOut;
    return {
        showIn,
        showOut,
        showIgnore: onlyDismiss || i.showIgnoreSetting,
        ignoreLabel: onlyDismiss ? 'Dismiss' : 'Ignore',
    };
}
